import Phaser from 'phaser'
import { THEME } from './theme'
import { alignPx, strokeWidthForCell } from './scale'

/**
 * Compact terrain/path sample for DEV gallery (M80).
 * Not used by match PlayingScene — proves production board language.
 */
export function paintBoardTerrainSample(
  g: Phaser.GameObjects.Graphics,
  originX: number,
  originY: number,
  cell: number
): void {
  const cols = 8
  const rows = 3
  const stroke = strokeWidthForCell(cell, 'thin')
  const grass = [0x345a27, 0x3d6b2e, 0x2e5022] as const

  g.fillStyle(THEME.board, 1)
  g.fillRoundedRect(
    originX - 6,
    originY - 6,
    cols * cell + 12,
    rows * cell + 12,
    Math.max(4, cell * 0.15)
  )
  g.lineStyle(stroke, THEME.boardEdge, 0.85)
  g.strokeRoundedRect(
    originX - 6,
    originY - 6,
    cols * cell + 12,
    rows * cell + 12,
    Math.max(4, cell * 0.15)
  )

  for (let y = 0; y < rows; y += 1) {
    for (let x = 0; x < cols; x += 1) {
      const px = alignPx(originX + x * cell)
      const py = alignPx(originY + y * cell)
      const onPath = y === 1 && x >= 1 && x <= 6
      if (onPath) {
        g.fillStyle(THEME.path, 1)
        g.fillRect(px, py, cell, cell)
        g.fillStyle(THEME.pathInner, 0.28)
        g.fillRect(px + cell * 0.15, py + cell * 0.22, cell * 0.7, cell * 0.56)
        g.fillStyle(THEME.pathEdge, 0.55)
        const edgeW = Math.max(1, cell * 0.06)
        g.fillRect(px, py, cell, edgeW)
        g.fillRect(px, py + cell - edgeW, cell, edgeW)
        if (x === 1) g.fillRect(px, py, edgeW, cell)
        if (x === 6) g.fillRect(px + cell - edgeW, py, edgeW, cell)
      } else {
        const tone = grass[(x + y * 3) % 3]!
        g.fillStyle(tone, 1)
        g.fillRect(px, py, cell, cell)
        if ((x + y) % 2 === 0) {
          g.fillStyle(THEME.grassAlt, 0.22)
          g.fillCircle(px + cell * 0.65, py + cell * 0.35, cell * 0.07)
        }
      }
    }
  }

  // Spawn A (circle) + exit diamond — shape + compact mark
  const midY = originY + cell * 1.5
  const sx = originX + cell * 1.5
  g.fillStyle(THEME.spawn, 0.2)
  g.fillCircle(sx, midY, cell * 0.28)
  g.lineStyle(strokeWidthForCell(cell, 'medium'), THEME.white, 1)
  g.strokeCircle(sx, midY, cell * 0.28)
  g.lineStyle(stroke, THEME.white, 0.95)
  const s = cell * 0.1
  g.beginPath()
  g.moveTo(sx - s, midY + s * 0.85)
  g.lineTo(sx, midY - s)
  g.lineTo(sx + s, midY + s * 0.85)
  g.strokePath()

  const ex = originX + cell * 6.5
  g.fillStyle(THEME.exit, 0.95)
  g.fillTriangle(ex, midY - cell * 0.28, ex + cell * 0.24, midY, ex, midY + cell * 0.28)
  g.fillTriangle(ex, midY - cell * 0.28, ex - cell * 0.24, midY, ex, midY + cell * 0.28)
  g.lineStyle(strokeWidthForCell(cell, 'medium'), THEME.selection, 0.9)
  g.strokeCircle(ex, midY, cell * 0.3)

  // Quiet AIR dashes across top row
  g.lineStyle(Math.max(1, cell * 0.03), THEME.airRoute, 0.35)
  const ay = originY + cell * 0.45
  for (let i = 0; i < 5; i += 1) {
    const x0 = originX + cell * (1.2 + i * 1.1)
    g.beginPath()
    g.moveTo(alignPx(x0), alignPx(ay))
    g.lineTo(alignPx(x0 + cell * 0.45), alignPx(ay))
    g.strokePath()
  }
}
